import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Icon } from "./ui";

type BoundaryProps = { readonly children: ReactNode; readonly pathname: string };
export type AppErrorBoundaryState = { readonly error: Error | null; readonly retryKey: number };

export const resetAppErrorBoundaryState = (state: AppErrorBoundaryState): AppErrorBoundaryState => ({
  error: null,
  retryKey: state.retryKey + 1,
});

export class AppErrorBoundary extends Component<BoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, retryKey: 0 };

  static getDerivedStateFromError(error: Error): Partial<AppErrorBoundaryState> { return { error }; }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Lawstream application error", error, info);
  }

  componentDidUpdate(previous: BoundaryProps): void {
    if (this.state.error !== null && previous.pathname !== this.props.pathname) this.setState({ error: null });
  }

  private retry = () => this.setState(resetAppErrorBoundaryState);

  render() {
    if (this.state.error === null) return <div key={this.state.retryKey}>{this.props.children}</div>;
    const documentRelated = this.props.pathname.startsWith("/documents")
      || this.props.pathname.startsWith("/mappings/document")
      || this.props.pathname.startsWith("/document-reviews")
      || this.props.pathname.startsWith("/policies")
      || this.props.pathname.startsWith("/mappings/policy")
      || this.props.pathname.startsWith("/policy-reviews");
    const recoveryPath = documentRelated ? "/documents" : "/browse";
    const recoveryLabel = documentRelated ? "Go to Documents" : "Go to Search";
    return (
      <div className="grid min-h-screen place-items-center bg-[var(--paper)] px-6">
        <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm" role="alert">
          <div className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-amber-50 text-amber-700"><Icon name="info" size={20} /></div>
          <h1 className="mt-4 font-serif text-xl font-semibold text-slate-900">Lawstream could not load this page.</h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">Try the page again, or return to a safe place in your workspace.</p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button onClick={this.retry}>Try again</Button>
            <Link to={recoveryPath} className="inline-flex h-9 items-center rounded-lg border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">{recoveryLabel}</Link>
          </div>
        </div>
      </div>
    );
  }
}

export function RoutedAppErrorBoundary({ children }: { readonly children: ReactNode }) {
  const location = useLocation();
  return <AppErrorBoundary pathname={location.pathname}>{children}</AppErrorBoundary>;
}
